import {
  Panel,
  PanelAction,
  RoutedPanelProps,
} from '@/components/Layout/Panel/Panel';
import { Tabs, TabsContent, TabsLink, TabsList } from '@/components/ui/tabs';
import { useProject } from '@/features/projects/projects.hooks';
import { DocsGroupTree } from '@/features/docs/docs.tree-view';
import { useDocsNotificationAction } from '@/features/docs/docs.notification-action';
import { TicketTable } from '@/features/tickets/ticket.table';
import { WorksTable } from '@/features/works/works.table';
import { useWorks } from '@/features/works/works.hooks';
import { useProjectTickets } from '@/features/tickets/tickets.hooks';
import { getProjectActions, adaptActionsForPanel } from './project.actions';
import { isInternalUser } from '@/app/navigation/access';
import { useCurrentUser } from '@/features/users/users.hooks';
import {
  TicketReportProvider,
  useTicketReportContext,
} from '@/hooks/useTicketReportContext';
import { useQueryClient } from '@tanstack/react-query';
import { FileDown } from 'lucide-react';
import { useMemo } from 'react';

// FIXME: Formulate and document queryKey strategy and use consistenly throughout app

function ProjectTickets({
  projectId,
  basePath,
}: {
  projectId: number;
  basePath: string;
}) {
  const res = useProjectTickets(projectId);
  return (
    <TicketTable
      projectLevel
      projectId={projectId}
      uriPrefix={basePath}
      {...res}
    />
  );
}

function ProjectWorks({
  projectId,
  basePath,
}: {
  projectId: number;
  basePath: string;
}) {
  const res = useWorks(projectId, { suspense: false });
  return <WorksTable uriPrefix={basePath} {...res} />;
}

function ProjectDocs({
  projectId,
  basePath,
}: {
  projectId: number;
  basePath: string;
}) {
  // Root docs view inside the project tab body. No selection (selection lives
  // on pushed group panels); the tab body's basePath terminates at /docs.
  return (
    <DocsGroupTree
      projectId={projectId}
      groupId={null}
      basePath={`${basePath}/docs`}
    />
  );
}

/**
 * Inner component that has access to TicketReportContext
 */
function ProjectDetailsPanelInner({
  params,
  path,
  basePath,
  ...panelProps
}: RoutedPanelProps) {
  const { projectId: projectIdStr } = params;
  const projectId = parseInt(projectIdStr);
  const { data } = useProject(projectId);
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const reportContext = useTicketReportContext();

  // External accounts only get the docs tab: the route closes the other
  // tabs (routes.ts), and without a tab in the URL docs is the landing tab.
  const internal = isInternalUser(currentUser);
  const tab: string = params.tab || (internal ? 'tickets' : 'docs');

  const docsNotificationAction = useDocsNotificationAction(projectId);

  const actions = useMemo(() => {
    const panelActions: PanelAction[] = [];

    // Add base project actions
    if (data) {
      const rowActions = getProjectActions(queryClient, currentUser);
      panelActions.push(...adaptActionsForPanel(rowActions, data));
    }

    // Add PDF report action (uses filtered tickets from context)
    if (reportContext && internal) {
      panelActions.push({
        icon: FileDown,
        label: 'Tickets PDF',
        sublabel:
          reportContext.filteredTicketRecnums.length > 0
            ? `${reportContext.filteredTicketRecnums.length} tickets`
            : null,
        onCalled: reportContext.downloadFilteredTicketsPdf,
        disabled:
          reportContext.isGeneratingPdf ||
          reportContext.filteredTicketRecnums.length === 0,
      });
    }

    // Docs notification subscription toggle.
    if (docsNotificationAction) {
      panelActions.push(docsNotificationAction);
    }

    return panelActions;
  }, [
    data,
    queryClient,
    currentUser,
    internal,
    reportContext,
    docsNotificationAction,
  ]);

  const title = !data
    ? `Project ${projectId}`
    : `${data.id} - ${data.projectname}`;

  if (!internal) {
    return (
      <Panel {...panelProps} actions={actions} title={title}>
        <ProjectDocs projectId={projectId} basePath={basePath} />
      </Panel>
    );
  }

  return (
    <Panel {...panelProps} actions={actions} title={title}>
      <Tabs value={tab} className="w-full">
        <TabsList className="w-full">
          <TabsLink to={`${basePath}/tickets`} value="tickets">
            Tickets
          </TabsLink>
          <TabsLink to={`${basePath}/works`} value="works">
            Works
          </TabsLink>
          <TabsLink to={`${basePath}/docs`} value="docs">
            Docs
          </TabsLink>
        </TabsList>
        <TabsContent value="tickets">
          <ProjectTickets projectId={projectId} basePath={basePath} />
        </TabsContent>
        <TabsContent value="works">
          <ProjectWorks projectId={projectId} basePath={basePath} />
        </TabsContent>
        <TabsContent value="docs">
          <ProjectDocs projectId={projectId} basePath={basePath} />
        </TabsContent>
      </Tabs>
    </Panel>
  );
}

/**
 * Project details panel with PDF report capability.
 * Wraps content in TicketReportProvider to enable filtered ticket PDF generation.
 */
export const ProjectDetailsPanel = (props: RoutedPanelProps) => {
  const projectId = parseInt(props.params.projectId);
  return (
    <TicketReportProvider projectId={projectId}>
      <ProjectDetailsPanelInner {...props} />
    </TicketReportProvider>
  );
};
